/**
 * A seat's own starting mind, shared by every activity that seeds simulated
 * students (a SimTeach puzzle, a Teach it back week). simteach mirrors it in
 * `app/agent/student_seed.py`.
 */
import {z} from 'zod';

/** A simulated student's mood. */
export const StudentEmotionSchema = z.enum(['happy', 'confused', 'disengaged']);

/** A simulated student's mood. */
export type StudentEmotion = z.infer<typeof StudentEmotionSchema>;

/** One starting belief with how firmly the student holds it. */
export const StudentThoughtSchema = z.object({
  /** The belief, in the student's words. */
  text: z.string().trim().min(1),
  /** How firmly it is held, 0 to 1 (1: certain). */
  confidence: z.number().min(0).max(1),
});

/** One starting belief with how firmly the student holds it. */
export type StudentThought = z.infer<typeof StudentThoughtSchema>;

/** One seat's own starting mind. Seats an activity omits keep the course defaults. */
export const StudentSeedSchema = z
  .object({
    /** The seat's first name, e.g. `sam` (case-insensitive). */
    seat: z.string().trim().min(1, 'Required'),
    /**
     * What this student believes at the start; misconceptions go here. A plain
     * string is held at full confidence. Empty: the course default beliefs (a
     * seat that only adds `knows`).
     */
    initialThoughts: z.array(
      z.union([z.string().trim().min(1), StudentThoughtSchema]),
    ),
    /** What this student is trying to do. Omitted: the course default. */
    initialGoal: z.string().optional(),
    /** The student's starting mood. Omitted: the persona's. */
    emotionalState: StudentEmotionSchema.optional(),
    /** Whether beliefs update after each of its exchanges. Omitted: false. */
    thinks: z.boolean().optional(),
    /**
     * Facts this student has, in their own frame: what was said or shown
     * earlier in class. Card students only. Omitted: what the room says plus
     * the course's knowledge.
     */
    knows: z.array(z.string().trim().min(1)).optional(),
    /**
     * Whether this student also has the course's what-students-have-seen list.
     * Card students only. Omitted: true.
     */
    usesCourseKnowledge: z.boolean().optional(),
  })
  .refine(
    seed => seed.initialThoughts.length > 0 || (seed.knows?.length ?? 0) > 0,
    {message: 'Add a belief or a fact', path: ['initialThoughts']},
  );

/** One seat's own starting mind. */
export type StudentSeed = z.infer<typeof StudentSeedSchema>;

/** An activity's seeds: at most one per seat, compared case-insensitively. */
export const StudentSeedsSchema = z
  .array(StudentSeedSchema)
  .superRefine((seeds, ctx) => {
    const seen = new Set<string>();
    seeds.forEach((seed, index) => {
      const seat = seed.seat.toLowerCase();
      if (seen.has(seat)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'seat'],
          message: 'Duplicate seat',
        });
      }
      seen.add(seat);
    });
  });
